import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../api/GlobalContext';
import { Card, Container, Row, CardHeader, Spinner } from 'reactstrap';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider from 'react-slick';
import Header from 'components/Headers/Header';
import {
  EMRS,
  NOTE_TYPES,
  PROFESSION_TYPES,
} from '../constants/EmrNoteTypeMapping';
import { getFirstEmrNoteType } from '../components/Misc/utils';

const sliderSettings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 5,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 2000,
  arrows: true,
  nextArrow: <div>▶</div>,
  responsive: [
    {
      breakpoint: 1024,
      settings: { slidesToShow: 5, slidesToScroll: 5 },
    },
    {
      breakpoint: 600,
      settings: { slidesToShow: 3, slidesToScroll: 3 },
    },
    {
      breakpoint: 480,
      settings: { slidesToShow: 3, slidesToScroll: 3 },
    },
  ],
};

const TemplateLibrary = () => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [libraryData, setLibraryData] = useState({
    mostUsed: [],
    emrs: [],
    isLoading: true,
  });

  useEffect(() => {
    fetchLibraryData();
  }, []);

  /**
   * Fetches all library data in a single API call
   * Updates the state with the fetched data
   */
  const fetchLibraryData = async () => {
    try {
      setLibraryData((prev) => ({ ...prev, isLoading: true }));
      const data = await apiClient.searchConfigsLibrary();

      const mostUsedTemplates = data?.configs?.top_configs?.length
        ? processMostUsedTemplates(data.configs.top_configs)
        : [];

      const processedEmrs =
        data?.emrs?.length > 0 ? processEmrs(data.emrs) : [];

      setLibraryData({
        mostUsed: mostUsedTemplates,
        emrs: processedEmrs,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching library data:', error);
      setLibraryData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const processMostUsedTemplates = (templates) => {
    const emrImages = templates.reduce((acc, note) => {
      if (note.emr) {
        acc[note.emr] = EMRS.find((e) => e.name === note.emr)?.image || '';
      }
      return acc;
    }, {});

    return templates.map((note) => ({
      emr: note.emr,
      note: getFirstEmrNoteType(note, note?.name || 'Template'),
      image: emrImages[note.emr],
      libraryConfigId: note.config_library_id,
    }));
  };

  const processEmrs = (emrs) => {
    return emrs.sort().map((emr) => ({
      name: emr,
      image: EMRS.find((e) => e.name === emr)?.image || '',
    }));
  };

  const renderSection = (title, items, index) => {
    const handleSeeAllClick = () => {
      const query =
        index === 2
          ? `?index=${index}&see-all=true&emr=${items[0]?.emr}&note=${items[1]?.note}`
          : `?index=${index}&see-all=true`;
      navigate(`/User/template/library/browse${query}`);
    };

    return (
      <div key={index} className="section-container">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="section-title" onClick={handleSeeAllClick}>
            {title}
          </h5>
          <h5 className="see-all text-muted" onClick={handleSeeAllClick}>
            {'See All'}
          </h5>
        </div>
        <Slider {...sliderSettings}>
          {items.map((item, idx) => (
            <Card
              className={`custom-card text-center p-3 ${index === 1 ? 'note-card' : index === 2 ? 'most-used-card' : index === 3 ? 'note-card' : 'emr-card'}`}
              key={idx}
            >
              {renderCardContent(item, index)}
            </Card>
          ))}
        </Slider>
      </div>
    );
  };

  const renderCardContent = (item, index) => {
    const handleClick = () => {
      if (index === 0) {
        navigate(`/User/template/library/browse?index=${1}&emr=${item.name}`);
      } else if (index === 1) {
        navigate(`/User/template/library/browse?index=${0}&note=${item.name}`);
      } else if (index === 2) {
        navigate(
          `/User/template/library/details?configId=${item.libraryConfigId}&emr=${item.emr}`,
        );
        sessionStorage.setItem('Library-List-Path', '');
        sessionStorage.setItem('Library-Browse-Path', '');
      } else if (index === 3) {
        navigate(
          `/User/template/library/browse?index=${3}&profession=${item.name}`,
        );
      }
    };

    return (
      <div
        className={`${index === 0 ? 'emr-content' : index === 1 ? 'note-content' : index === 3 ? 'note-content' : 'most-used-content'}`}
        onClick={handleClick}
      >
        <img
          src={item.image}
          alt={
            index === 0
              ? item.name
              : index === 1
                ? item.name
                : index === 3
                  ? item.name
                  : item.emr
          }
          className={
            index === 0
              ? 'emr-logo'
              : index === 1
                ? 'note-image'
                : index === 3
                  ? 'note-image'
                  : 'most-used-emr-logo'
          }
        />
        <h5
          className={
            index === 2
              ? 'most-used-text'
              : index === 3
                ? 'note-name'
                : 'note-name'
          }
        >
          {index === 2 ? item.note : item.name}
        </h5>
      </div>
    );
  };

  const renderSections = () => {
    const sections = [
      {
        title: 'Browse by EMRs',
        items: libraryData.emrs,
        index: 0,
      },
      {
        title: 'Browse by Note Type',
        items: NOTE_TYPES,
        index: 1,
      },
      {
        title: 'Top 15 Most Used Notes',
        items: libraryData.mostUsed,
        index: 2,
      },
      {
        title: 'Browse by Discipline',
        items: PROFESSION_TYPES,
        index: 3,
      },
    ];

    return sections.map((section) =>
      renderSection(section.title, section.items, section.index),
    );
  };

  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow border-0">
              <CardHeader className="border border-2 border-grey bg-white text-left d-flex justify-content-between align-items-center">
                <h2 className="mb-0 font-weight-bold">Template Library</h2>
                {libraryData.isLoading && <Spinner color="primary" />}
              </CardHeader>
              <Container className="mt-4" fluid>
                {renderSections()}
                <style>{`
                                    /* Slider Navigation */
                                    .slick-next:before {
                                        font-size: 40px;
                                        color: rgb(222, 219, 219);
                                        z-index: 10;
                                        cursor: pointer;
                                        position: absolute;
                                        right: 5px;
                                        left: auto;
                                    }

                                    /* Section Layout */
                                    .section-container {
                                        margin: -2px 0 10px;
                                        z-index: 1;
                                    }

                                    /* Section Headers */
                                    .section-title {
                                        font-size: 1.2rem;
                                        margin-bottom: 1px;
                                        cursor: pointer;
                                    }

                                    .see-all {
                                        font-size: 1rem;
                                        font-weight: normal;
                                        color: grey;
                                        cursor: pointer;
                                    }

                                    /* Card Base Styles */
                                    .custom-card {
                                        width: 100px;
                                        height: 140px;
                                        border-radius: 15px;
                                        padding: 10px;
                                        transition: transform 0.3s ease-in-out;
                                        border: 5px solid white;
                                    }

                                    .custom-card:hover {
                                        transform: scale(1.05);
                                        cursor: pointer;
                                    }

                                    /* Card Variants */
                                    .emr-card, .most-used-card {
                                        background: rgb(237, 240, 246);
                                    }

                                    .note-card {
                                        background: linear-gradient(135deg, #A0B4F1, #8EE4AF);
                                    }

                                    /* Card Content */
                                    .emr-content, .note-content, profession-content, .most-used-content {
                                        font-size: 0.8rem;
                                        display: flex;
                                        flex-direction: column;
                                        align-items: center;
                                        text-align: center;                                       @extend .flex-center;
                                    }

                                    /* Image Sizes */
                                    .note-image, .most-used-emr-logo, .most-used-note-image, .emr-logo, .profession-image {
                                        object-fit: contain;
                                    }

                                    .note-image, .most-used-emr-logo, .most-used-note-image, .profession-image {
                                        width: 50px;
                                        height: 50px;
                                    }

                                    .emr-logo {
                                        width: 80px;
                                        height: 80px;
                                    }

                                    /* Text Styles */
                                    .note-name, .most-used-text, .profession-text {
                                        font-size: 0.8rem;
                                        margin-top: 10px;
                                    }

                                    /* Responsive Styles */
                                    @media (max-width: 768px) {
                                        .slick-next:before, .slick-prev:before {
                                            font-size: 30px;
                                        }

                                        .section-title {
                                            font-size: 1rem;
                                        }

                                        .see-all {
                                            font-size: 0.8rem;
                                        }

                                        .note-name, .most-used-text, .profession-text {
                                            font-size: 0.7rem;
                                            margin-top: 0;
                                        }

                                        .note-card, .most-used-card, .profession-card {
                                            width: 60px;
                                            height: 120px;
                                        }
                                    }
                                `}</style>
              </Container>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default TemplateLibrary;
